import React, { useEffect, useRef, useState } from 'react';
import {
  Circle,
  GoogleMap,
  LoadScript,
  Marker,
  Polygon,
  Polyline,
} from '@react-google-maps/api';
import { DeliveryDataObject, Location } from '../../types';
import AddDeliveryAreaModal from './AddDeliveryAreaModal';
import toast, { Toaster } from 'react-hot-toast';
import { addDeliveryAreas } from '../../firebase-logic/deliveryArea';
import {
  canFinishDeliveryArea,
  normalizeDeliveryAreaPath,
} from '../../firebase-logic/deliveryAreaPath';
import useAuthState from '../../global-states/useAuthState';
import { getCompanyLocation } from '../../firebase-logic/settings';
import CustomLoadingScreen from '../CustomLoadingScreen';
import { useTranslation } from 'react-i18next';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../config';

type Props = {
  isEditing: boolean;
  setIsEditing: Function;
  deliveryAreas: DeliveryDataObject[];
  setDeliveryAreas: Function;
};

const containerStyle = {
  width: '100%',
  height: '100%',
};

const savedPolygonOptions = {
  fillOpacity: 0.3,
  strokeOpacity: 0.5,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  geodesic: false,
  zIndex: 1,
};

const draftShapeOptions = {
  fillColor: '#0ea5e9',
  fillOpacity: 0.2,
  strokeColor: '#0ea5e9',
  strokeOpacity: 0.9,
  strokeWeight: 3,
  clickable: false,
  editable: false,
  draggable: false,
  zIndex: 2,
};

function DeliveryMap(props: Props) {
  const auth: any = useAuthState.getState();
  const { isEditing, setIsEditing, deliveryAreas, setDeliveryAreas } = props;
  const { t } = useTranslation(['common']);
  const polygonRefs = useRef<Record<string, any>>({});
  const [openAddDeliveryAreaModal, setOpenAddDeliveryAreaModal] =
    useState(false);
  const [draftPath, setDraftPath] = useState<Location[]>([]);
  const [center, setCenter] = useState<any>(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapLoadError, setMapLoadError] = useState(false);

  const googleMapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    mapId: '85ff4664989e45fb',
    draggableCursor: isEditing ? 'crosshair' : undefined,
    disableDoubleClickZoom: isEditing,
  };

  useEffect(() => {
    getLocation(auth.user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditing) {
      setDraftPath([]);
      toast(t('drawMap.label'), {
        icon: '✏️',
        position: 'top-center',
        className: 'mt-12',
      });
    } else {
      setDraftPath([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const getLocation = async (user: any) => {
    setMapLoading(true);
    try {
      const companyLocation = await getCompanyLocation(user);
      if (!companyLocation?.geoCode) {
        throw new Error('Company location is missing a geocode.');
      }
      setCenter(companyLocation.geoCode);
    } catch (error) {
      console.error('Unable to load the delivery map location', error);
      setMapLoadError(true);
    } finally {
      setMapLoading(false);
    }
  };

  const handleMapClick = (event: any) => {
    if (!isEditing || openAddDeliveryAreaModal || !event.latLng) {
      return;
    }

    const point = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setDraftPath((currentPath) => [...currentPath, point]);
  };

  const handleUndoPoint = () => {
    setDraftPath((currentPath) => currentPath.slice(0, -1));
  };

  const handleFinishDrawing = () => {
    if (!canFinishDeliveryArea(draftPath)) {
      return;
    }

    setDraftPath(normalizeDeliveryAreaPath(draftPath));
    setOpenAddDeliveryAreaModal(true);
  };

  const handleAddDeliveryArea = async (
    deliveryAreaDetails: DeliveryDataObject
  ) => {
    const path = normalizeDeliveryAreaPath(draftPath);
    if (!canFinishDeliveryArea(path)) {
      throw new Error('A delivery area requires at least three points.');
    }

    const newDeliveryArea = {
      ...deliveryAreaDetails,
      path,
    };
    const updatedDeliveryAreas = [...deliveryAreas, newDeliveryArea];

    await addDeliveryAreas(auth.user, updatedDeliveryAreas);
    setDeliveryAreas(updatedDeliveryAreas);
  };

  const handleCancel = () => {
    setDraftPath([]);
    setIsEditing(false);
  };

  const handlePolygonEdit = async (deliveryArea: DeliveryDataObject) => {
    const polygon = polygonRefs.current[deliveryArea.id];
    if (!polygon) {
      return;
    }

    const path = normalizeDeliveryAreaPath(
      polygon
        .getPath()
        .getArray()
        .map((point: any) => ({ lat: point.lat(), lng: point.lng() }))
    );
    const updatedDeliveryArea = { ...deliveryArea, path };
    const updatedDeliveryAreas = deliveryAreas.map((area) =>
      area.id === updatedDeliveryArea.id ? updatedDeliveryArea : area
    );

    await addDeliveryAreas(auth.user, updatedDeliveryAreas);
    setDeliveryAreas(updatedDeliveryAreas);
  };

  const handlePolygonLoad = (areaId: string, polygon: any) => {
    polygonRefs.current[areaId] = polygon;
  };

  const handlePolygonUnmount = (areaId: string) => {
    delete polygonRefs.current[areaId];
  };

  return (
    <div className='w-full h-full relative'>
      {mapLoadError ? (
        <div className='w-full h-full flex items-center justify-center p-6 text-center text-gray-600'>
          {t('mapLoadError.label')}
        </div>
      ) : (
        <LoadScript
          googleMapsApiKey={REACT_APP_GOOGLE_MAPS_API_KEY}
          onError={() => setMapLoadError(true)}
        >
          {!mapLoading && center ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={13}
              options={googleMapOptions}
              onClick={handleMapClick}
            >
              {deliveryAreas.map((area) => (
                <Polygon
                  key={area.id}
                  paths={area.path}
                  editable={!isEditing}
                  draggable={false}
                  onMouseUp={() => {
                    toast.promise(handlePolygonEdit(area), {
                      loading: t('updating.label'),
                      success: t('updateSuccess.label'),
                      error: t('somethingWentWrong.label'),
                    });
                  }}
                  options={{
                    ...savedPolygonOptions,
                    fillColor: area.color,
                    strokeColor: area.color,
                  }}
                  onLoad={(polygon) => handlePolygonLoad(area.id, polygon)}
                  onUnmount={() => handlePolygonUnmount(area.id)}
                />
              ))}

              {draftPath.length >= 3 ? (
                <Polygon paths={draftPath} options={draftShapeOptions} />
              ) : draftPath.length >= 2 ? (
                <Polyline path={draftPath} options={draftShapeOptions} />
              ) : null}

              {draftPath.map((point, index) => (
                <Circle
                  key={`${point.lat}-${point.lng}-${index}`}
                  center={point}
                  radius={12}
                  options={{
                    fillColor: '#0ea5e9',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    clickable: false,
                    zIndex: 3,
                  }}
                />
              ))}

              <Marker
                position={{ ...center }}
                icon='https://firebasestorage.googleapis.com/v0/b/droov-group-1.appspot.com/o/assets%2FstoreIcon.svg?alt=media&token=d5f50357-121b-4742-a136-79c36f256c20'
                title='Your store'
              />
            </GoogleMap>
          ) : (
            <CustomLoadingScreen />
          )}
        </LoadScript>
      )}

      {isEditing && !openAddDeliveryAreaModal && (
        <div
          className='absolute z-20 flex items-center bg-white rounded-lg shadow-lg p-2'
          style={{ bottom: 24, left: '50%', transform: 'translateX(-50%)' }}
        >
          <span className='text-sm text-gray-500 px-2 whitespace-nowrap'>
            {draftPath.length}/3
          </span>
          <button
            type='button'
            className={`cancel_button h-9 px-4 mr-2 whitespace-nowrap ${
              draftPath.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={draftPath.length === 0}
            onClick={handleUndoPoint}
          >
            {t('undoPoint.button')}
          </button>
          <button
            type='button'
            className={`relative_blue_button h-9 px-4 mr-2 whitespace-nowrap ${
              !canFinishDeliveryArea(draftPath)
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            disabled={!canFinishDeliveryArea(draftPath)}
            onClick={handleFinishDrawing}
          >
            {t('finishArea.button')}
          </button>
          <button
            type='button'
            className='relative_red_button h-9 px-4 whitespace-nowrap'
            onClick={handleCancel}
          >
            {t('cancel.button')}
          </button>
        </div>
      )}

      {openAddDeliveryAreaModal && (
        <AddDeliveryAreaModal
          openAddDeliveryAreaModal={openAddDeliveryAreaModal}
          closeAddDeliveryAreaModal={setOpenAddDeliveryAreaModal}
          handleAddDeliveryArea={handleAddDeliveryArea}
          handleCancel={handleCancel}
        />
      )}
      <Toaster position='bottom-center' reverseOrder={false} />
    </div>
  );
}

export default DeliveryMap;
